<div class="quiz-take container-xxl py-6 py-lg-10">

  @if (isLoading()) {
    <div class="card"><div class="card-body is-empty"><span class="spinner-border text-primary"></span></div></div>
  } @else if (attempt(); as a) {

    <!-- The chrome: what paper am I sitting, and how far in am I. It stays put
         between the question and the review screen, so the last "next" does not
         look like it navigated somewhere else. -->
    <header class="quiz-chrome">
      <div class="quiz-chrome__row d-flex align-items-start justify-content-between">
        <div>
          <span class="quiz-chrome__eyebrow d-block fw-bold text-uppercase">{{ "QUIZ.TITLE" | translate }}</span>
          <h1 class="quiz-chrome__title fw-bold m-0">{{ a.quiz_title }}</h1>
        </div>
        @if (!reviewing()) {
          <span class="quiz-chrome__count d-flex align-items-baseline flex-shrink-0" data-testid="question-index">
            <span class="quiz-chrome__now fs-0 fw-bolder lh-1">{{ index() + 1 }}</span>
            <span class="quiz-chrome__of fw-medium">/ {{ total() }}</span>
          </span>
        }
      </div>

      <!-- The answer sheet, always on screen.
           This was a rail of unlabelled 8px segments: it showed how many were
           left but you could not aim at one, because "go to question 7" meant
           counting bars. The 2021 exam screen solved the same problem with a
           row of numbered chips carrying per-question state, and it is plainly
           better — the number is the target and the fill is the state, in one
           object. Ported in this palette (sky for answered, filled brand for
           where you are) rather than that build's magenta, and without its
           section tabs and pagination arrows: our papers are drawn from a bank,
           are one section, and are short enough to show whole. -->
      <ol class="quiz-sheetnav d-flex flex-wrap m-0 p-0" [attr.aria-label]="'QUIZ.REVIEW' | translate">
        @for (question of questions(); track question.id; let i = $index) {
          <li>
            <button type="button" class="quiz-sheetnav__chip p-0 rounded-circle fw-bold lh-1"
                    [class.is-done]="!!answers()[question.id]"
                    [class.is-current]="!reviewing() && i === index()"
                    [attr.aria-label]="('QUIZ.QUESTION' | translate) + ' ' + (i + 1)"
                    [attr.aria-current]="!reviewing() && i === index() ? 'step' : null"
                    (click)="goTo(i)">{{ i + 1 }}</button>
          </li>
        }
      </ol>
    </header>

    @if (reviewing()) {
      <section class="quiz-sheet">
        <h2 class="quiz-sheet__head fw-bold" data-testid="review-heading">{{ "QUIZ.REVIEW" | translate }}</h2>

        @if (unanswered() > 0) {
          <p class="quiz-note m-0 is-warn" data-testid="unanswered-notice">
            {{ "QUIZ.UNANSWERED" | translate }} {{ unanswered() }} {{ "QUIZ.ITEMS" | translate }}
          </p>
        } @else {
          <p class="quiz-note m-0 is-ok" data-testid="all-answered-notice">
            {{ "QUIZ.ALL_ANSWERED" | translate }}
          </p>
        }

        <!-- A grid rather than a list: with 20+ questions the point is to spot
             the gaps at a glance and jump straight to one. -->
        <div class="quiz-grid d-grid">
          @for (question of questions(); track question.id; let i = $index) {
            <button type="button" class="quiz-grid__cell fw-bold"
                    [class.is-done]="!!answers()[question.id]"
                    (click)="goTo(i)">{{ i + 1 }}</button>
          }
        </div>

        <div class="quiz-actions d-flex justify-content-end">
          <button class="btn btn-light order-2 order-sm-1" type="button" (click)="back()">
            {{ "QUIZ.BACK" | translate }}
          </button>
          <button class="btn btn-primary order-1 order-sm-2" type="button"
                  [disabled]="isSubmitting()" data-testid="submit-answers" (click)="submit()">
            {{ isSubmitting() ? ("QUIZ.SUBMITTING" | translate) : ("QUIZ.SUBMIT" | translate) }}
          </button>
        </div>
      </section>
    } @else if (current(); as question) {
      <article class="quiz-sheet">
        <div class="quiz-sheet__meta d-flex align-items-center flex-wrap">
          <span class="quiz-sheet__no fw-bold text-uppercase">{{ "QUIZ.QUESTION" | translate }} {{ index() + 1 }}</span>
          @if (question.sub_lesson_name) {
            <span class="quiz-chip lh-base">{{ question.sub_lesson_name }}</span>
          }
        </div>

        <p class="quiz-sheet__body" data-testid="question-body">{{ question.body }}</p>

        @if (question.question_type === 'choice') {
          <div class="quiz-choices d-flex flex-column">
            @for (choice of question.choices; track choice.key) {
              <!-- `btn-light-primary` is the selected state an E2E case asserts
                   on; the look comes from `.quiz-choice`, which outranks it. -->
              <button type="button" class="quiz-choice d-flex align-items-center" data-testid="choice"
                      [class.btn-light-primary]="isChosen(question.id, choice.key)"
                      [class.is-chosen]="isChosen(question.id, choice.key)"
                      [attr.aria-pressed]="isChosen(question.id, choice.key)"
                      (click)="choose(question.id, choice.key)">
                <span class="quiz-choice__key d-flex align-items-center justify-content-center fw-bold fs-7">{{ choice.key }}</span>
                <span class="quiz-choice__text flex-fill">{{ choice.text }}</span>
                <span class="quiz-choice__tick rounded-circle" aria-hidden="true"></span>
              </button>
            }
          </div>
        } @else {
          <input class="quiz-answer w-100" type="text"
                 data-testid="short-answer"
                 [placeholder]="'QUIZ.TYPE_ANSWER' | translate"
                 [ngModel]="answers()[question.id] || ''"
                 (ngModelChange)="setText(question.id, $event)" />
        }

        <div class="quiz-actions d-flex justify-content-end">
          <button class="btn btn-light" type="button" [disabled]="index() === 0" (click)="back()">
            {{ "QUIZ.BACK" | translate }}
          </button>
          <button class="btn btn-primary" type="button"
                  data-testid="advance" (click)="advance()">
            {{ index() >= total() - 1 ? ("QUIZ.TO_REVIEW" | translate) : ("QUIZ.NEXT" | translate) }}
          </button>
        </div>
      </article>
    }
  }
</div>
